import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  BarChart3,
  CalendarDays,
  Home as HomeIcon,
  Settings as SettingsIcon,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { TabBar, type TabDef } from '../components';
import { useWalkthrough } from '../walkthrough';
import { ActivityScreen } from './activity/ActivityScreen';
import { AnalyticsScreen } from './analytics/AnalyticsScreen';
import { PlanScreen } from './plan/PlanScreen';
import { SettingsScreen } from './settings/SettingsScreen';
import { TodayScreen } from './today/TodayScreen';

export type TabId = 'today' | 'activity' | 'plan' | 'insights' | 'settings';

/**
 * Optional one-shot intent passed when navigating to a tab — lets a
 * screen open in a particular state. Currently only Plan honors it
 * (open the Add Income sheet on landing). Cleared after consumption.
 */
export type TabAction = 'addIncome' | null;

const TABS: TabDef<TabId>[] = [
  { id: 'today', label: 'Today', icon: HomeIcon },
  { id: 'activity', label: 'Activity', icon: Activity },
  { id: 'plan', label: 'Plan', icon: CalendarDays },
  { id: 'insights', label: 'Insights', icon: BarChart3 },
  { id: 'settings', label: 'Settings', icon: SettingsIcon },
];

/**
 * Top-level navigation shell. Renders the active screen with a quick
 * crossfade and pins the bottom TabBar above the safe-area inset.
 * Tabs switch only via the bottom nav buttons; horizontal swipe is
 * intentionally not handled here so child scroll containers (charts,
 * pill rows, forecast cards) keep ownership of horizontal gestures.
 */
export function TabNavigator() {
  const [active, setActiveState] = useState<TabId>('today');
  const [planAction, setPlanAction] = useState<TabAction>(null);
  const walkthrough = useWalkthrough();

  const navigate = useCallback(
    (next: TabId, action?: TabAction) => {
      if (action === 'addIncome') setPlanAction('addIncome');
      if (next === active) return;
      setActiveState(next);
    },
    [active],
  );

  const setActive = useCallback((next: TabId) => navigate(next), [navigate]);

  useEffect(() => {
    if (!walkthrough.active) return;
    const targetTab = walkthrough.step.tab;
    if (targetTab && targetTab !== active) {
      setActive(targetTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walkthrough.active, walkthrough.step.id, setActive]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="min-h-full bg-bg"
    >
      <div className="pb-[max(env(safe-area-inset-bottom),20px)]">
        <div className="pb-[64px]">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={active}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {active === 'today' ? (
                <TodayScreen onNavigate={navigate} />
              ) : null}
              {active === 'activity' ? <ActivityScreen /> : null}
              {active === 'plan' ? (
                <PlanScreen
                  initialAction={planAction}
                  onInitialActionConsumed={() => setPlanAction(null)}
                />
              ) : null}
              {active === 'insights' ? <AnalyticsScreen /> : null}
              {active === 'settings' ? <SettingsScreen /> : null}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Hide the TabBar while the walkthrough panel is up — the panel
          drives navigation via Next/Back so the bar is redundant and
          would sit underneath. */}
      {walkthrough.active ? null : (
        <TabBar tabs={TABS} active={active} onChange={setActive} />
      )}
    </motion.div>
  );
}
